import { z } from 'zod';
import { AnnotationSchema, DefectTypeSchema, InspectionSchema, ProductSchema, } from './entities.js';
import { RegionsSchema } from './regions.js';
import { AnnotationLabelSchema, DefectClassificationSchema, DeviceSchema, ModelBackendSchema, VerdictSchema, } from './verdicts.js';
// ── Products ────────────────────────────────────────────────
export const CreateProductSchema = z.object({
    name: z.string().min(1),
    sku: z.string().min(1),
    /**
     * Whose product this is. Required at birth.
     *
     * It used to be absent from this schema and from the INSERT, so every product
     * created through the UI came into the world unowned and stayed that way
     * until somebody noticed — which on this installation took weeks and hid the
     * majority of the inspections from any customer report. A product acquires an
     * owner at the moment it acquires a name, or the gap is one more thing to
     * remember.
     */
    customerId: z.number().int().positive(),
    active: z.boolean().default(true),
    /**
     * Which question this line asks — see ../tasks.ts.
     *
     * Chosen when the product is created, because it decides which screens exist
     * for it and which shape its inspections take. Defaults to anomaly, which is
     * what every product was before classification existed.
     */
    task: z.enum(['anomaly', 'classification']).default('anomaly'),
    /** anomaly only: score >= threshold is BAD. */
    threshold: z.number().min(0).max(1).default(0.5),
    uncertaintyBand: z.number().min(0).max(0.5).default(0),
    modelVersion: z.string().nullable().optional(),
});
export const UpdateProductSchema = CreateProductSchema.partial();
// ── Defect types ────────────────────────────────────────────
export const CreateDefectTypeSchema = z.object({
    name: z.string().min(1),
    color: z
        .string()
        .regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, 'Must be a hex color like #ff3b30'),
    /** What picking this defect implies: GOOD (acceptable), BAD (reject), UNSURE. */
    classification: DefectClassificationSchema.default('BAD'),
    description: z.string().max(500).nullable().optional(),
    sortOrder: z.number().int().default(0),
});
export const UpdateDefectTypeSchema = CreateDefectTypeSchema.partial();
// ── Inspect ─────────────────────────────────────────────────
/** Multipart: a file field `image` + a form field `productId`. Body schema covers the non-file part. */
export const InspectFormSchema = z.object({
    productId: z.coerce.number().int().positive(),
});
export const InspectResultSchema = z.object({
    id: z.string().uuid(),
    /**
     * Null on a classification line, which answers with a class instead.
     *
     * Left in the same response rather than split into two, because the caller —
     * the HAL bridge, a manual upload, a station — asks the same question either
     * way: "here are the bytes, what is it?". What comes back differs, and the
     * task registry says which fields to read.
     */
    verdict: VerdictSchema.nullable(),
    classId: z.number().int().positive().nullable().default(null),
    classConfidence: z.number().nullable().default(null),
    decision: z.enum(['auto', 'reject', 'override']).nullable().default(null),
    score: z.number(),
    /**
     * Where the image was stored. The caller just supplied the bytes but has no
     * other way to address them afterwards — annotating what you just inspected
     * needed a second round trip to look this up.
     */
    imageKey: z.string(),
    heatmapKey: z.string().nullable(),
    latencyMs: z.number().int().nonnegative(),
});
// ── Inspections list ────────────────────────────────────────
export const ListInspectionsQuerySchema = z.object({
    productId: z.coerce.number().int().positive().optional(),
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
    verdict: VerdictSchema.optional(),
    defectTypeId: z.coerce.number().int().positive().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    /** Opaque cursor from a previous page's `nextCursor`. */
    cursor: z.string().optional(),
});
export const ListInspectionsResultSchema = z.object({
    items: z.array(InspectionSchema),
    nextCursor: z.string().nullable(),
});
// ── Stats ───────────────────────────────────────────────────
export const StatsWindowSchema = z.enum(['1h', '24h', '7d', 'all']);
export const StatsQuerySchema = z.object({
    productId: z.coerce.number().int().positive().optional(),
    /** Roll the whole dashboard up to one customer (their products' inspections). */
    customerId: z.coerce.number().int().positive().optional(),
    window: StatsWindowSchema.default('24h'),
});
export const StatsBucketSchema = z.object({
    t: z.string(),
    good: z.number().int().nonnegative(),
    bad: z.number().int().nonnegative(),
    unsure: z.number().int().nonnegative(),
    avgLatencyMs: z.number().nonnegative(),
});
/** Top defect types found by humans in the window (ranked). */
export const DefectCountSchema = z.object({
    defectTypeId: z.number().int().nullable(),
    name: z.string(),
    color: z.string(),
    count: z.number().int().nonnegative(),
});
/** One class's share of what was predicted in the window (ranked). */
export const ClassCountSchema = z.object({
    classId: z.number().int().nullable(),
    code: z.string(),
    name: z.string(),
    count: z.number().int().nonnegative(),
    /** Mean confidence of the predictions that landed here, 0–1. */
    avgConfidence: z.number().min(0).max(1),
});
export const ClassificationBucketSchema = z.object({
    t: z.string(),
    /** Predictions that named a class. */
    predicted: z.number().int().nonnegative(),
    /** Predictions the model declined to make — no class was confident enough. */
    rejected: z.number().int().nonnegative(),
    avgLatencyMs: z.number().nonnegative(),
});
/**
 * The line's quality question: is this part acceptable?
 *
 * Every field here is about verdicts and is counted over anomaly inspections
 * only. See StatsOverviewSchema for why that is a separate object rather than a
 * filter someone has to remember to apply.
 */
export const AnomalyStatsSchema = z.object({
    total: z.number().int().nonnegative(),
    good: z.number().int().nonnegative(),
    bad: z.number().int().nonnegative(),
    unsure: z.number().int().nonnegative(),
    goodRatio: z.number().min(0).max(1),
    throughputPerMin: z.number().nonnegative(),
    avgLatencyMs: z.number().nonnegative(),
    /** Inspections still awaiting a human label — the annotation backlog. */
    unannotated: z.number().int().nonnegative(),
    defects: z.array(DefectCountSchema),
    buckets: z.array(StatsBucketSchema),
});
/**
 * The line's identity question: which product is this?
 *
 * No verdict, no pass rate, no defects — none of those mean anything about a
 * crate of shoulder. What matters instead is the spread across classes, how
 * often the model declined to answer, and how often a person had to correct it.
 */
export const ClassificationStatsSchema = z.object({
    total: z.number().int().nonnegative(),
    /** Named a class. */
    predicted: z.number().int().nonnegative(),
    /** Declined to name one, so it went to a person. Not a failure — a refusal. */
    rejected: z.number().int().nonnegative(),
    /** A person disagreed with the prediction and set a different class. */
    corrected: z.number().int().nonnegative(),
    /** Predicted, and no person has confirmed or corrected it yet. */
    unreviewed: z.number().int().nonnegative(),
    throughputPerMin: z.number().nonnegative(),
    avgLatencyMs: z.number().nonnegative(),
    avgConfidence: z.number().min(0).max(1),
    classes: z.array(ClassCountSchema),
    buckets: z.array(ClassificationBucketSchema),
});
/**
 * The dashboard, split by the question the line is asking.
 *
 * The two tasks live in separate objects because they were flat, and being flat
 * is what broke it. `total` counted every inspection while `good` counted only
 * the ones with a verdict, so each classified crate — which has no verdict and
 * never will — quietly dragged the pass rate down. On real data the dashboard
 * read 34.5% when the line's actual pass rate was 44.1%, and nothing on the
 * screen suggested the number was mixing two different questions.
 *
 * Nested, the arithmetic that produced that cannot be written down. There is no
 * `total` to divide by that spans both, and a pass rate over classifications
 * has nowhere to come from — the same reason the products table carries a
 * composite key on (id, task).
 *
 * Either may be null: a line asking only one of the two questions gets one
 * block, not an empty panel of zeroes implying it ran nothing.
 */
export const StatsOverviewSchema = z.object({
    window: StatsWindowSchema,
    anomaly: AnomalyStatsSchema.nullable(),
    classification: ClassificationStatsSchema.nullable(),
    /**
     * The newest inspection regardless of window, or null if there has never been
     * one. Lets an empty window say "quiet since Tuesday" rather than the same
     * sentence an installation that has never run would show.
     */
    lastInspectionAt: z.string().nullable().default(null),
});
// ── Reporting: per customer, per product ────────────────────
/**
 * One product's line in a report.
 *
 * Carries its task so the reader knows which figures mean anything. A product
 * is one or the other and never both — the products table has a unique key on
 * (id, task) precisely so nothing can be ambiguous here — so exactly one of the
 * two blocks is populated and the other is null. Not "zero", which would read
 * as a line that ran and judged nothing.
 */
export const ProductReportSchema = z.object({
    productId: z.number().int(),
    productName: z.string(),
    task: z.enum(['anomaly', 'classification']),
    /** Populated on an anomaly product, null on a classification one. */
    anomaly: AnomalyStatsSchema.nullable(),
    /** Populated on a classification product, null on an anomaly one. */
    classification: ClassificationStatsSchema.nullable(),
});
/**
 * One customer's report.
 *
 * The customer-level figures are per task and never summed across them. There
 * is no single number here saying how a customer's line "did", because the two
 * questions have no common unit: a pass rate and an agreement rate are not
 * averageable, and a customer running both would otherwise get a headline that
 * moves when the mix changes rather than when the line does.
 */
export const CustomerReportSchema = z.object({
    /**
     * Null for products belonging to no customer yet.
     *
     * Such products are reported under one "not assigned" entry rather than
     * omitted. On this installation they held 1,163 of 1,384 inspections — the
     * majority — and a report that quietly left them out would have looked
     * complete and been wrong by more than it was right. An empty group is a
     * question someone can answer; a missing one is not.
     */
    customerId: z.number().int().nullable(),
    customerName: z.string(),
    /** Pill tint; null for the unassigned group, which has no customer row. */
    customerHue: z.number().int().nullable().default(null),
    anomaly: AnomalyStatsSchema.nullable(),
    classification: ClassificationStatsSchema.nullable(),
    products: z.array(ProductReportSchema),
});
export const CustomerReportsSchema = z.object({
    window: StatsWindowSchema,
    customers: z.array(CustomerReportSchema),
});
// ── Annotations ─────────────────────────────────────────────
export const CreateAnnotationSchema = z.object({
    inspectionId: z.string().uuid().nullable().optional(),
    imageKey: z.string().min(1),
    /**
     * A quality judgement, on an anomaly product only.
     *
     * Optional now, because a classification annotation asserts an identity
     * instead — see classId. Which of the two is required is decided by the
     * product's task, not by this schema; see annotationFits() in ./tasks.ts.
     */
    label: AnnotationLabelSchema.nullable().optional(),
    /** The class this image belongs to, on a classification product. */
    classId: z.number().int().positive().nullable().optional(),
    /** Needed to resolve the task when no inspection is attached. */
    productId: z.number().int().positive().nullable().optional(),
    defectTypeId: z.number().int().positive().nullable().optional(),
    regions: RegionsSchema,
    annotatedBy: z.string().nullable().optional(),
});
export const AnnotationQueueQuerySchema = z.object({
    productId: z.coerce.number().int().positive().optional(),
    /** Restrict the queue to one uploaded image set (Dataset -> "Annotate this set"). */
    setId: z.coerce.number().int().positive().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(25),
});
/** A queue item: an inspection awaiting annotation, plus its uncertainty distance. */
export const AnnotationQueueItemSchema = InspectionSchema.extend({
    /** |score − threshold|; smaller = more uncertain = higher priority. */
    uncertainty: z.number().nonnegative(),
});
// ── Images ──────────────────────────────────────────────────
export const PresignedUrlSchema = z.object({
    key: z.string(),
    url: z.string().url(),
    expiresIn: z.number().int().positive(),
});
// ── Health ──────────────────────────────────────────────────
export const HealthComponentSchema = z.object({
    ok: z.boolean(),
    detail: z.string().optional(),
});
export const InferenceHealthSchema = z.object({
    status: z.literal('ok'),
    device: DeviceSchema,
    backend: ModelBackendSchema,
    // snake_case to match the Python service wire format.
    model_version: z.string(),
});
export const HealthSchema = z.object({
    ok: z.boolean(),
    db: HealthComponentSchema,
    minio: HealthComponentSchema,
    inference: HealthComponentSchema,
    gpu: z.object({ available: z.boolean(), device: DeviceSchema.optional() }),
});
// ── WebSocket live feed ─────────────────────────────────────
/** Message pushed on /ws/live for every new inspection. */
export const LiveInspectionEventSchema = z.object({
    type: z.literal('inspection'),
    inspection: InspectionSchema,
});
// Re-export commonly used entity schemas for convenience.
export { ProductSchema, DefectTypeSchema, InspectionSchema, AnnotationSchema };
//# sourceMappingURL=api.js.map